import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Trash2, Copy, Plus, X, Search, Key, Eye, EyeOff } from 'lucide-react';
import { partnersApi, type Partner } from '../../api/endpoints/partners';
import { queryClient } from '../../providers/QueryProvider';
import { Card, StatCard } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Table, Thead, Th, Td, Tr } from '../../components/ui/Table';
import { Pagination } from '../../components/ui/Pagination';
import { Modal } from '../../components/ui/Modal';
import { PageSpinner } from '../../components/ui/Spinner';
import { useDebounce } from '../../hooks/useDebounce';
import { formatDate, formatTimeAgo } from '../../utils/formatters';

const API_LABELS: Record<string, string> = {
  me: 'Partner Info (me)',
  tunnels: 'List Clients (tunnels)',
  tunnels_count: 'Count Clients (tunnels_count)',
  proxy: 'Proxy Forward (proxy)',
  broadcast: 'Broadcast (broadcast)',
  call: 'Direct Call (call)',
  call_wildcard: 'Wildcard Call (call_wildcard)',
};

const API_KEYS = Object.keys(API_LABELS) as Array<keyof typeof API_LABELS>;

export function PartnerDetailPage() {
  const { id: partnerId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showKeyRevealed, setShowKeyRevealed] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [showAddTunnel, setShowAddTunnel] = useState(false);
  const [tunnelSearch, setTunnelSearch] = useState('');
  const [tunnelPage, setTunnelPage] = useState(1);
  const [availableSearch, setAvailableSearch] = useState('');
  const [addIp, setAddIp] = useState('');
  const [addIpDesc, setAddIpDesc] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [editingStatus, setEditingStatus] = useState(false);

  const debouncedTunnelSearch = useDebounce(tunnelSearch);
  const debouncedAvailableSearch = useDebounce(availableSearch);

  // Queries
  const { data: partnerData, isLoading } = useQuery({
    queryKey: ['partner', partnerId],
    queryFn: () => partnersApi.get(partnerId!),
    enabled: !!partnerId,
  });

  const { data: tunnelsData } = useQuery({
    queryKey: ['partner-tunnels', partnerId, tunnelPage, debouncedTunnelSearch],
    queryFn: () => partnersApi.getTunnels(partnerId!, { page: tunnelPage, limit: 20, search: debouncedTunnelSearch }),
    enabled: !!partnerId,
  });

  const { data: availableData } = useQuery({
    queryKey: ['available-tunnels', debouncedAvailableSearch],
    queryFn: () => partnersApi.availableTunnels(debouncedAvailableSearch, 50),
    enabled: showAddTunnel,
  });

  // Mutations
  const updateMutation = useMutation({
    mutationFn: (data: Partial<Partner>) => partnersApi.update(partnerId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => partnersApi.delete(partnerId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      navigate('/partners');
    },
  });

  const regenerateKeyMutation = useMutation({
    mutationFn: () => partnersApi.regenerateKey(partnerId!),
    onSuccess: (result) => {
      setRevealedKey(result.api_key);
      setShowKeyRevealed(true);
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
    },
  });

  const addTunnelMutation = useMutation({
    mutationFn: (clientId: string) => partnersApi.addTunnel(partnerId!, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-tunnels', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['available-tunnels'] });
    },
  });

  const removeTunnelMutation = useMutation({
    mutationFn: (clientId: string) => partnersApi.removeTunnel(partnerId!, clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-tunnels', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      queryClient.invalidateQueries({ queryKey: ['available-tunnels'] });
    },
  });

  const addIpMutation = useMutation({
    mutationFn: () => partnersApi.addWhitelistIp(partnerId!, addIp, addIpDesc),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
      setAddIp('');
      setAddIpDesc('');
    },
  });

  const removeIpMutation = useMutation({
    mutationFn: (ip: string) => partnersApi.removeWhitelistIp(partnerId!, ip),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
    },
  });

  const toggleApiMutation = useMutation({
    mutationFn: ({ api, enabled }: { api: string; enabled: boolean }) =>
      partnersApi.updateApiAccess(partnerId!, { [api]: enabled }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner', partnerId] });
    },
  });

  function copyText(text: string) {
    navigator.clipboard.writeText(text);
  }

  function handleSaveName() {
    if (nameInput.trim()) {
      updateMutation.mutate({ name: nameInput.trim() } as Partial<Partner>);
    }
    setEditingName(false);
  }

  function handleStatusChange(status: string) {
    updateMutation.mutate({ status } as Partial<Partner>);
    setEditingStatus(false);
  }

  if (isLoading) return <PageSpinner />;

  const partner = partnerData?.partner;
  if (!partner) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">Partner not found</p>
        <Link to="/partners" className="text-purple-600 text-sm mt-2 inline-block">Back to partners</Link>
      </div>
    );
  }

  const tunnels = tunnelsData?.tunnels || [];
  const tunnelPagination = tunnelsData?.pagination;
  const availableTunnels = availableData?.tunnels || [];
  const enabledApis = partner.settings?.enabled_apis || {};
  const enabledCount = API_KEYS.filter(k => (enabledApis as Record<string, boolean>)[k] !== false).length;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/partners')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              {editingName ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={nameInput}
                    onChange={e => setNameInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleSaveName(); if (e.key === 'Escape') setEditingName(false); }}
                    autoFocus
                    className="text-lg font-bold text-gray-900 dark:text-white border border-purple-300 dark:border-purple-600 rounded px-2 py-0.5 focus:ring-1 focus:ring-purple-500 dark:bg-gray-700 w-56"
                  />
                  <button onClick={handleSaveName} className="text-green-600 hover:text-green-800 text-xs">Save</button>
                  <button onClick={() => setEditingName(false)} className="text-gray-400 hover:text-gray-600 text-xs">Cancel</button>
                </div>
              ) : (
                <h1
                  className="text-lg font-bold text-gray-900 dark:text-white cursor-pointer hover:text-purple-600 dark:hover:text-purple-400"
                  onClick={() => { setNameInput(partner.name); setEditingName(true); }}
                  title="Click to edit"
                >
                  {partner.name}
                </h1>
              )}
              <span className="cursor-pointer" onClick={() => setEditingStatus(!editingStatus)}>
                <Badge
                  variant={partner.status === 'active' ? 'green' : partner.status === 'suspended' ? 'red' : 'yellow'}
                  dot
                >
                  {partner.status}
                </Badge>
              </span>
              {editingStatus && (
                <div className="flex items-center gap-1">
                  {['active', 'inactive', 'suspended'].filter(s => s !== partner.status).map(s => (
                    <button
                      key={s}
                      onClick={() => handleStatusChange(s)}
                      className="text-[10px] px-2 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">{partner.partner_id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { if (confirm('Delete this partner? This cannot be undone.')) deleteMutation.mutate(); }}
            className="px-3 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 flex items-center gap-1"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      </div>

      {/* Usage Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <StatCard label="Total Requests" value={partner.usage?.total_requests || 0} color="purple" />
        <StatCard label="Success" value={partner.usage?.successful_requests || 0} color="green" />
        <StatCard label="Failed" value={partner.usage?.failed_requests || 0} color="red" />
        <StatCard label="Tunnels" value={partner.authorized_tunnels?.length || 0} color="blue" />
        <StatCard label="APIs" value={`${enabledCount}/7`} color="yellow" />
      </div>

      {/* 2-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-4">
        {/* Left: API Key + Details */}
        <div className="lg:col-span-3 space-y-4">
          {/* API Key */}
          <Card title="API Key" action={
            <button
              onClick={() => { if (confirm('Regenerate API key? The current key will stop working immediately.')) regenerateKeyMutation.mutate(); }}
              className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 flex items-center gap-1"
              disabled={regenerateKeyMutation.isPending}
            >
              <Key size={12} />
              {regenerateKeyMutation.isPending ? 'Regenerating...' : 'Regenerate'}
            </button>
          }>
            <div className="p-4">
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-md font-mono text-xs text-gray-800 dark:text-gray-200">
                  pk_{'*'.repeat(32)}
                </code>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">API key is hashed and cannot be retrieved. Regenerate if needed.</p>
              </div>
            </div>
          </Card>

          {/* Partner Details */}
          <Card title="Details">
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                ['Partner ID', partner.partner_id],
                ['Contact', partner.contact?.name || '-'],
                ['Email', partner.contact?.email || '-'],
                ['Description', partner.description || '-'],
                ['Created', formatDate(partner.createdAt)],
                ['Last Request', partner.usage?.last_request ? formatTimeAgo(partner.usage.last_request) : 'Never'],
                ['Last IP', partner.usage?.last_ip || '-'],
                ['Rate Limit', `${partner.rate_limit?.requests_per_minute || 100}/min`],
                ['Require IP Whitelist', partner.settings?.require_ip_whitelist ? 'Yes' : 'No'],
              ].map(([label, value]) => (
                <div key={label as string}>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{label}</p>
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200 break-all">{String(value)}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* IP Whitelist */}
          <Card title="IP Whitelist" action={
            <span className="text-xs text-gray-500 dark:text-gray-400">{partner.whitelisted_ips?.length || 0} IPs</span>
          }>
            <div className="p-4">
              {/* Add IP form */}
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={addIp}
                  onChange={e => setAddIp(e.target.value)}
                  placeholder="IP or CIDR (e.g., 10.0.0.0/8)"
                  className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs font-mono dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
                />
                <input
                  type="text"
                  value={addIpDesc}
                  onChange={e => setAddIpDesc(e.target.value)}
                  placeholder="Description"
                  className="w-40 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-xs dark:bg-gray-700 dark:text-white focus:ring-1 focus:ring-purple-500"
                />
                <button
                  onClick={() => { if (addIp.trim()) addIpMutation.mutate(); }}
                  disabled={!addIp.trim() || addIpMutation.isPending}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                >
                  <Plus size={12} />
                  Add
                </button>
              </div>
              {addIpMutation.error && (
                <p className="text-xs text-red-600 dark:text-red-400 mb-2">{(addIpMutation.error as Error).message}</p>
              )}
              {/* IP list */}
              <div className="space-y-1">
                {partner.whitelisted_ips?.map(w => (
                  <div key={w.ip} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 rounded px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono text-gray-800 dark:text-gray-200">{w.ip}</code>
                      {w.description && <span className="text-[10px] text-gray-400">- {w.description}</span>}
                    </div>
                    <button
                      onClick={() => removeIpMutation.mutate(w.ip)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                {(!partner.whitelisted_ips || partner.whitelisted_ips.length === 0) && (
                  <p className="text-xs text-gray-400 text-center py-2">No IPs whitelisted</p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Right: API Access */}
        <div className="lg:col-span-2 space-y-4">
          <Card title="API Access" action={
            <Badge variant={enabledCount === 7 ? 'green' : 'yellow'}>{enabledCount}/7 enabled</Badge>
          }>
            <div className="p-4 space-y-2">
              {API_KEYS.map(apiKey => {
                const enabled = (enabledApis as Record<string, boolean>)[apiKey] !== false;
                return (
                  <div key={apiKey} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 rounded px-3 py-2">
                    <div className="flex items-center gap-2">
                      {enabled ? <Eye size={14} className="text-green-500" /> : <EyeOff size={14} className="text-gray-400" />}
                      <span className="text-xs text-gray-800 dark:text-gray-200">{API_LABELS[apiKey]}</span>
                    </div>
                    <button
                      onClick={() => toggleApiMutation.mutate({ api: apiKey, enabled: !enabled })}
                      disabled={toggleApiMutation.isPending}
                      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        enabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        enabled ? 'translate-x-4' : 'translate-x-0'
                      }`} />
                    </button>
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Quick Info */}
          <Card title="Quick Info">
            <div className="p-4 space-y-3">
              <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-2 text-center">
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Status</p>
                <p className="text-sm font-semibold capitalize">{partner.status}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-2 text-center">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">Tunnels</p>
                  <p className="text-sm font-semibold">{partner.authorized_tunnels?.length || 0}</p>
                </div>
                <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 p-2 text-center">
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">IPs</p>
                  <p className="text-sm font-semibold">{partner.whitelisted_ips?.length || 0}</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Authorized Tunnels */}
      <Card title="Authorized Tunnels" className="mb-4" action={
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1.5 text-gray-400" />
            <input
              type="text"
              value={tunnelSearch}
              onChange={e => { setTunnelSearch(e.target.value); setTunnelPage(1); }}
              placeholder="Search tunnels..."
              className="pl-7 pr-3 py-1 border border-gray-300 dark:border-gray-600 rounded text-xs dark:bg-gray-700 dark:text-white w-48 focus:ring-1 focus:ring-purple-500"
            />
          </div>
          <button onClick={() => setShowAddTunnel(true)} className="bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700 flex items-center gap-1">
            <Plus size={12} />
            Add
          </button>
        </div>
      }>
        <Table>
          <Thead>
            <tr>
              <Th>Client</Th>
              <Th>Deployment</Th>
              <Th>Status</Th>
              <Th>Port</Th>
              <Th>Last Seen</Th>
              <Th>Added</Th>
              <Th>Actions</Th>
            </tr>
          </Thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {tunnels.map(tunnel => (
              <Tr key={tunnel._id}>
                <Td>
                  <div>
                    <Link to={`/clients/${tunnel.client_id}`} className="font-medium text-purple-600 dark:text-purple-400 hover:underline text-xs">
                      {tunnel.client?.client_name || tunnel.alias || tunnel.client_id}
                    </Link>
                    <p className="text-[10px] text-gray-400">{tunnel.client_id}</p>
                  </div>
                </Td>
                <Td>{tunnel.client?.deployment_name || '-'}</Td>
                <Td>
                  <Badge
                    variant={tunnel.client?.status === 'connected' ? 'green' : tunnel.client?.status === 'disconnected' ? 'red' : 'gray'}
                    dot
                  >
                    {tunnel.client?.status || 'unknown'}
                  </Badge>
                </Td>
                <Td>{tunnel.client?.port || '-'}</Td>
                <Td>{tunnel.client?.last_seen ? formatTimeAgo(tunnel.client.last_seen) : '-'}</Td>
                <Td>{formatDate(tunnel.createdAt)}</Td>
                <Td>
                  <button
                    onClick={() => {
                      if (confirm(`Remove ${tunnel.client_id} from this partner?`)) {
                        removeTunnelMutation.mutate(tunnel.client_id);
                      }
                    }}
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 text-xs font-medium"
                  >
                    Remove
                  </button>
                </Td>
              </Tr>
            ))}
            {tunnels.length === 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-xs text-gray-500 dark:text-gray-400">No authorized tunnels. Add clients to grant partner access.</td></tr>
            )}
          </tbody>
        </Table>
        {tunnelPagination && (
          <div className="px-4 pb-3">
            <Pagination page={tunnelPagination.page} totalPages={tunnelPagination.pages} onPageChange={setTunnelPage} />
          </div>
        )}
      </Card>

      {/* Add Tunnel Modal */}
      <Modal open={showAddTunnel} onClose={() => setShowAddTunnel(false)} title="Add Authorized Client" wide>
        <div className="space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2 text-gray-400" />
            <input
              type="text"
              value={availableSearch}
              onChange={e => setAvailableSearch(e.target.value)}
              placeholder="Search by name, deployment, ID..."
              className="w-full pl-8 pr-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-1 focus:ring-purple-500 text-xs dark:bg-gray-700 dark:text-white"
            />
          </div>
          {addTunnelMutation.error && (
            <p className="text-xs text-red-600 dark:text-red-400">{(addTunnelMutation.error as Error).message}</p>
          )}
          <div className="max-h-80 overflow-y-auto space-y-1">
            {availableTunnels.map(tunnel => (
              <div key={tunnel.client_id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-900/50 rounded px-3 py-2">
                <div>
                  <p className="text-xs font-medium text-gray-800 dark:text-gray-200">{tunnel.deployment_name || tunnel.client_name}</p>
                  <p className="text-[10px] text-gray-400">{tunnel.client_id} {tunnel.hostname ? `/ ${tunnel.hostname}` : ''}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={tunnel.status === 'connected' ? 'green' : 'gray'} dot>{tunnel.status}</Badge>
                  <button
                    onClick={() => addTunnelMutation.mutate(tunnel.client_id)}
                    disabled={addTunnelMutation.isPending}
                    className="px-2 py-0.5 bg-purple-600 text-white rounded text-xs hover:bg-purple-700 disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            ))}
            {availableTunnels.length === 0 && (
              <p className="text-center text-xs text-gray-500 dark:text-gray-400 py-4">No available clients. All deployed clients are already assigned.</p>
            )}
          </div>
          <p className="text-[10px] text-gray-500 dark:text-gray-400">
            Only clients with a deployment assigned are shown. Total available: {availableData?.total_available || 0}
          </p>
        </div>
      </Modal>

      {/* Regenerated Key Modal */}
      <Modal open={showKeyRevealed} onClose={() => { setShowKeyRevealed(false); setRevealedKey(null); }} title="New API Key Generated">
        <div className="space-y-3">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
            <p className="text-yellow-800 dark:text-yellow-300 text-xs font-medium">Save this key now! It will not be shown again.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">New API Key</label>
            <div className="flex">
              <code className="flex-1 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-l-md font-mono text-xs overflow-x-auto text-gray-800 dark:text-gray-200">{revealedKey}</code>
              <button onClick={() => copyText(revealedKey || '')} className="bg-gray-200 dark:bg-gray-600 px-3 rounded-r-md hover:bg-gray-300 dark:hover:bg-gray-500 text-xs">
                <Copy size={14} />
              </button>
            </div>
          </div>
          <button
            onClick={() => { setShowKeyRevealed(false); setRevealedKey(null); }}
            className="w-full px-4 py-2 bg-purple-600 text-white rounded-md text-xs hover:bg-purple-700"
          >
            Done
          </button>
        </div>
      </Modal>
    </div>
  );
}
